<script setup lang="ts">
import { PieChart } from 'echarts/charts'
import { use } from 'echarts/core'
import { computed } from 'vue'
import VChart from 'vue-echarts'

import { CHART_TOOLTIP } from '~/environment-variables/chart.const'
import { getColorSetByLength } from '~/utils/color'

interface IProps {
  size?: number
  data: {
    name: string,
    value: string
  }[]
  formatter: (params: any) => string
  tooltip?: any
}

const props = withDefaults(defineProps<IProps>(), {
  size: 210,
  tooltip: {
    backgroundColor: CHART_TOOLTIP.backgroundColor,
    textStyle: CHART_TOOLTIP.textStyle,
  },
})

const chartSize = computed(() => {
  return `${props.size}px`
})


const chart = ref(null)


use([ PieChart ])

const parsedData = computed(() => {
  const colorSet = getColorSetByLength(props.data.length)
  return props.data.map((item, idx) => {
    return {
      ...item,
      itemStyle: { color: colorSet[idx] },
    }
  })
})

const unChecked = ref<string[]>([])

const clickItem = (i: string) => {
  if (!chart.value) return

  if (unChecked.value.includes(i)) unChecked.value = unChecked.value.filter(item => item !== i)
  else unChecked.value.push(i)

  chart.value.dispatchAction({
    type: 'legendToggleSelect',
    name: i,
  })
}

const itemList = computed(() => {
  const colorSet = getColorSetByLength(props.data.length)
  return parsedData.value.map((i, idx) => {
    return {
      ...i,
      color: colorSet[idx],
      isChecked: !unChecked.value.includes(i.name),
    }
  })
})

const pieChartOption = ref({
  tooltip: {
    trigger: 'item',
    formatter: props.formatter,
    backgroundColor: props.tooltip.backgroundColor,
    padding: 8,
    borderWidth: 0,
    textStyle: props.tooltip.textStyle,
  },
  label: {
    show: false,
  },
  legend: {
    show: false,
    orient: 'vertical',
    left: '60%',
    top: 'middle',
    itemWidth: 8,
    itemHeight: 8,
    textStyle: {
      fontSize: 12,
      color: 'rgb(13 27 58 / 0.4)'
    },
    formatter: function (name: string) {
      const maxLength = 25
      if (name.length > maxLength) return name.slice(0, maxLength) + '...'
      else return name
    },
    // data: parsedData.value.map(item => item.name),
  },
  series: [
    {
      name: 'Traffic Sources',
      type: 'pie',
      label: {
        show: false,
        position: 'center'
      },
      radius: [ '80%', '90%' ],
      itemStyle: {
        borderRadius: 10,
        borderColor: '#111928',
        borderWidth: 5,
        borderOpacity: 0.4,
      },
      data: parsedData.value,
    },
  ],
})
</script>

<template>
  <div class="pie-chart-container">
    <div
      class="chart-cont"
    >
      <VChart
        class="chart"
        :option="pieChartOption"
        :style="{ height: chartSize, width: chartSize }"
        autoresize
        ref="chart"
      />
      <div class="center-label">
        <slot />
      </div>
    </div>
    <div class="legend-cont">
      <div
        v-for="i in itemList"
        :key="i.name"
        class="legend-item"
        :class="{ active: i.isChecked }"
        @click="clickItem(i.name)"
      >
        <div
          class="point"
          :style="{ backgroundColor: i.color }"
        />
        <span class="name">{{ i.name }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pie-chart-container {
  @apply grid grid-cols-2;

  .chart-cont {
    @apply flex items-center justify-center;
    position: relative;

    .center-label {
      @apply absolute top-0 left-0 flex justify-center items-center w-full h-full;
      pointer-events: none;
    }
  }

  .legend-cont {
    @apply flex flex-col justify-center pl-2;

    .legend-item {
      @apply flex items-center text-primary/40 mb-1 cursor-pointer select-none opacity-50 transition-opacity;
      font-size: 12px;

      .point {
        @apply mr-2 shrink-0 w-2 h-2 rounded-full;
      }

      .name {
        @apply truncate;
      }
    }

    .legend-item.active {
      @apply opacity-100;
    }
  }
}
</style>